// NPM
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { filter, find, map, omitBy, isUndefined, isEmpty, isNil } from 'lodash';

// UTILS
import errorHandler from 'Utils/error-handler';

// ANT COMPONENTS
import { Space, Row, Col } from 'antd';
import { AlertBox, Button, TextInput, PhoneInput, SelectInput, RadioGroup, RadioChangeEvent } from '@life-line-lab/components-react';

// JSON VALUES
import country from 'Data/country.json';
import state from 'Data/state.json';

// STYLE
import './corporate-order-form.scss';


// VERIFICATION
import CorporateOrderFormSchema from './corporate-order-form.verification';

// HELPERS
import { toaster } from 'Helpers/toaster';

interface Input {
  orgName: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  fullName: string;
  contactNumber: { dialCode: string; number: string; };
  email: string;
  kitsCount: string;
  onSiteScreening: string;
}

interface CorporateOrderFormProps {
  status?: string;
  expectedError?: any,
  onSuccess?: () => void,
  onSubmit: (input: any) => void;
}
let recaptchaToken="";

const serverData = (data: any) => {

  if (isEmpty(data)) { return {} };

  let contactNumber: string | null = null;
  if (data.contactNumber?.dialCode && data.contactNumber?.number) {
    contactNumber = data.contactNumber.dialCode + data.contactNumber.number;
  }

  return omitBy({
    companyName: data.orgName,
    street1: data.addressLine1,
    street2: data.addressLine2,
    city: data.city,
    postcode: data.postcode,
    state: data.state,
    country: data.country,
    fullName: data.fullName,
    contactNumber: contactNumber,
    emailAddress: data.email,
    kitsCount: data.kitsCount,
    requiredServices: data.onSiteScreening
  }, o => isNil(o) || o.trim() === '');
}

const CorporateOrderForm: React.FC<CorporateOrderFormProps> = ({ status, expectedError, onSuccess, onSubmit }) => {
  const initialInput = {
    orgName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postcode: '',
    country: 'MY',
    fullName: '',
    contactNumber: { dialCode: '60', number: '' },
    email: '',
    kitsCount: '50',
    onSiteScreening: ''
  }

  const initialError = {};
  const initialStateList = map((filter(state, (o: any) => o.CountryCode === 'MY')),
    (o: any) => { return { value: o.Code, label: o.Name } }
  );

  const { t } = useTranslation();

  const [input, setInput] = useState<Input>(initialInput);
  const [error, setError] = useState<any>(initialError);
  const [stateList, setStateList] = useState<any[]>(initialStateList);

  useEffect(() => {
    if (expectedError.message && status === 'error') {
      toaster('error', t(expectedError.message));
    }
    else if (status === 'success') {
      setInput(initialInput);
      onSuccess && onSuccess();
    }
  }, [expectedError.message, status]);

  useEffect(() => {
    const selected = find(country, (o: any) => o.Code === input.country);
    const stateList = map((filter(state, (o: any) => o.CountryCode === selected?.Code)),
      (o: any) => { return { value: o.Code, label: o.Name } }
    );
    setStateList(stateList);
  }, [input.country])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(initialError);
    const value: any = e.target.value;

    if (e.target.name === 'kitsCount') {
      if (!isNaN(value)) {
        setInput({ ...input, [e.target.name]: e.target.value });
      }
      else {
        setError({
          ...error,
          [e.target.name]: t('error.kitsCountNumeric', { field: e.target.name })
        });
      }
    }
    else {
      setInput({ ...input, [e.target.name]: e.target.value });
    }
  }

  const handlePhoneChange = (value: { dialCode: string, number: string }, name: string) => {
    setError(initialError);
    value = !isNil(value) ? value : { dialCode: '60', number: '' };
    setInput({ ...input, [name]: value });
  }

  const handleSelectChange = (value: string, name: string) => {
    setError(initialError);

    if (name === 'country') {
      const selected = find(country, (o: any) => o.Code === value);
      const stateList = map((filter(state, (o: any) => o.CountryCode === selected?.Code)),
        (o: any) => { return { value: o.CODE, label: o.NAME } }
      );
      setStateList(stateList);
      setInput({ ...input, [name]: value, state: '' });
    }
    else {
      setInput({ ...input, [name]: value });
    }
  }

  const handleRadioChange = (value: string, name: string,) => {
    setError(initialError);
    setInput({ ...input, [name]: value });
  }

  const handleSubmit = () => {

    try {
      const value = CorporateOrderFormSchema.validateSync(omitBy({
        ...input
      }, isUndefined), {
        strict: true,
        abortEarly: true,
        stripUnknown: false
      });

      const countryName = find(country, elem => elem.Code === input.country)?.Name;
      const stateName = find(stateList, elem => elem.value === input.state)?.label;

      if (!isEmpty(value)) {
        if (parseInt(value.kitsCount) < 50 ) {
          setError({
            ...error,
            kitsCount: t('error.kitsCountMinumum')
          });
        }
        else {
          onSubmit(serverData({ ...value, country: countryName, state: stateName }));
        }
      }
    }
    catch (err) {
      let { path, value }: any = errorHandler.validation(err);

      // e.g. contactNumber.number = contactNumber
      if (path.indexOf('.') !== -1) {
        const str = path.split('.');
        path = str[0];
      }

      if (path === 'kitsCount') {
        setError({ ...error, [path]: t('error.kitsCount') });
      }
      else if (path === 'onSiteScreening') {
        setError({ ...error, [path]: t('error.onSiteScreening') });
      }
      else {
        setError({ ...error, [path]: t(`${value}`, { field: t(`label.${path}`) }) });
      }

      if (document.querySelector(`input[name=${path}]`)) {
        (document.querySelector(`input[name=${path}]`) as HTMLInputElement).focus();
      }
      else if (document.querySelector(`div[name=${path}]`)) {
        (document.querySelector(`div[name=${path}] input`) as HTMLInputElement).focus();
      }
    }
  }

  return (
    <div className="corporate-order-form">
      <form>
        <Space className="supplier-form--inner" direction="vertical" size="large">
          <div>
            <TextInput
              isRequired={true}
              isError={!!error.orgName}
              label={t('label.companyName')}
              name="orgName"
              value={input.orgName}
              onChange={handleChange}
            />
            <AlertBox text={error.orgName} />
          </div>

          <div>
            <TextInput
              isRequired={true}
              isError={!!error.addressLine1}
              label={t('label.addressLine1')}
              name="addressLine1"
              value={input.addressLine1}
              onChange={handleChange}
            />
            <AlertBox text={error.addressLine1} />
          </div>

          <div>
            <TextInput
              isError={!!error.addressLine2}
              label={t('label.addressLine2')}
              name="addressLine2"
              value={input.addressLine2}
              onChange={handleChange}
            />
            <AlertBox text={error.addressLine2} />
          </div>

          <Row gutter={16} style={{ marginBottom: '-20px' }}>
            <Col className="mb-20" xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
              <TextInput
                isRequired={true}
                isError={!!error.city}
                label={t('label.city')}
                name="city"
                value={input.city}
                onChange={handleChange}
              />
              <AlertBox text={error.city} />
            </Col>

            <Col className="mb-20" xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
              <TextInput
                isRequired={true}
                isError={!!error.postcode}
                label={t('label.postcode')}
                name="postcode"
                value={input.postcode}
                onChange={handleChange}
              />
              <AlertBox text={error.postcode} />
            </Col>
          </Row>

          <Row gutter={16} style={{ marginBottom: '-20px' }}>
            <Col className="mb-20" xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
              <SelectInput
                isError={!!error.state}
                label={t('label.state')}
                name="state"
                value={input.state}
                showSearch={true}
                placeholder="Select one"
                onChange={
                  (value: any) =>
                    handleSelectChange(value, 'state')
                }
                filterOption={(input: string, option: any) =>
                  option.props.value.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
                  option.props.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                isRequired={true}
                options={stateList}
              />
              <AlertBox text={error.state} />
            </Col>

            <Col className="mb-20" xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
              <SelectInput
                isError={!!error.businessCountry}
                label={t('label.country')}
                name="country"
                value={input.country}
                showSearch={true}
                placeholder="Select one"
                onChange={
                  (value: any) =>
                    handleSelectChange(value, 'country')
                }
                filterOption={(input: string, option: any) =>
                  option.props.value.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
                  option.props.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                isRequired={true}
                options={map(country, (o) => {
                  return {
                    value: o.Code,
                    label: o.Name
                  }
                })}
              />
              <AlertBox text={error.country} />
            </Col>
          </Row>

          <div>
            <TextInput
              isRequired={true}
              isError={!!error.fullName}
              label={t('label.fullName')}
              name="fullName"
              value={input.fullName}
              onChange={handleChange}
            />
            <AlertBox text={error.fullName} />
          </div>

          <Row gutter={16} style={{ marginBottom: '-20px' }}>
            <Col className="mb-20" xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
              <PhoneInput
                isRequired={true}
                isError={!!error.contactNumber}
                label={t('label.contactNumber')}
                name="contactNumber"
                value={input.contactNumber}
                onChange={(value) => handlePhoneChange(value, 'contactNumber')}
              />
              <AlertBox text={error.contactNumber} />
            </Col>

            <Col className="mb-20" xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
              <TextInput
                isRequired={true}
                isError={!!error.email}
                label={t('label.emailAddress')}
                name="email"
                value={input.email}
                onChange={handleChange}
              />
              <AlertBox text={error.email} />
            </Col>
          </Row>

          <Row gutter={16} style={{ marginBottom: '-20px' }}>
            <Col className="mb-20" xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
              <TextInput
                isRequired={true}
                isError={!!error.kitsCount}
                label={t('label.kitsCount')}
                name="kitsCount"
                value={input.kitsCount}
                onChange={handleChange}
              />
              <AlertBox text={error.kitsCount} />
            </Col>

            <Col className="mb-20" xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
              <RadioGroup
                direction="horizontal"
                isRequired={true}
                isError={!!error.onSiteScreening}
                label={t('label.onSiteScreening')}
                name="onSiteScreening"
                value={input.onSiteScreening}
                onChange={(event: RadioChangeEvent) =>
                  handleRadioChange(event.target.value, 'onSiteScreening')}
                options={[
                  { value: 'Yes', label: t('option.yes') },
                  { value: 'No', label: t('option.no') },
                ]}
              />
              <AlertBox text={error.onSiteScreening} />
            </Col>
          </Row>

          <div className="form--actions">
            <Button
              isLoading={status === "loading"}
              type="primary"
              label={t('action.submit')}
              size="large"
              onClick={handleSubmit}
            />
          </div>
        </Space>
      </form>
    </div>
  )
}

export default CorporateOrderForm;
